import { Button } from "@/components/ui/button";
import usePage from "@/hooks/usePage";
import { useForm } from "react-hook-form";
import { Link } from "@inertiajs/react";
import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { PlusIcon } from "lucide-react";
import { formatDistance } from "date-fns";

import { TextField } from "@/components/TextField/TextField";
import { usePublicationSubdomain } from "@/hooks/usePublicationSubdomain";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import EmptyStateImage from "./empty-state.svg?react";

type ContentEntriesPageProps = {
  contentEntries: ContentEntry[];
  newContentEntryPath: string;
};

export const columns: ColumnDef<ContentEntry>[] = [
  {
    header: "Title",
    accessorFn: ({ title, slug }) => ({ title, slug }),
    cell: ({ cell }) => {
      const subdomain = usePublicationSubdomain();
      const { title, slug } = cell.getValue() as {
        title: string;
        slug: string;
      };

      return (
        <Link
          href={`${subdomain}/content/${slug}`}
          className="font-medium hover:underline"
        >
          {title}
        </Link>
      );
    },
  },
  {
    header: "Published",
    accessorFn: (row) =>
      row.publishedAt
        ? formatDistance(new Date(row.publishedAt), new Date(), {
            addSuffix: true,
          })
        : "-",
  },
  {
    header: "Created",
    accessorFn: (row) =>
      formatDistance(new Date(row.createdAt), new Date(), {
        addSuffix: true,
      }),
  },
  {
    header: "Updated",
    accessorFn: (row) =>
      formatDistance(new Date(row.lastUpdatedAt), new Date(), {
        addSuffix: true,
      }),
  },
];

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
}

export default function ContentEntries() {
  const { register } = useForm<{ "content_entry[title]": string }>();
  const { props } = usePage<AdminUsePageProps & ContentEntriesPageProps>();
  const table = useReactTable({
    data: props.contentEntries,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (props.contentEntries.length === 0) {
    return (
      <div className="flex items-center justify-center w-full h-full">
        <div className="flex flex-col items-start justify-center max-w-md">
          <EmptyStateImage width={128} height={128} className="mb-2" />
          <h2 className="text-2xl font-medium">
            Start Building Your Collection
          </h2>
          <p className="mt-2 text-gray-600 text-pretty">
            Create content entries to populate your publication with books,
            posts, or any material you'd like to share. Each entry can be
            organized, edited, and published when you're ready.
          </p>
          <form
            action={props.newContentEntryPath}
            className="flex items-center gap-1 w-full mt-4"
            method="POST"
          >
            <input
              type="hidden"
              name="authenticity_token"
              value={props.csrf_token}
            />
            <TextField
              placeholder="Start with a title, or leave blank..."
              inputSize="sm"
              autoComplete="off"
              data-1p-ignore
              wrapperClassName="!mt-0"
              {...register("content_entry[title]")}
            />
            <Button type="submit" size="sm">
              Create First Entry
            </Button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="mx-2 mt-2 mb-2 px-2 h-10 rounded flex items-center justify-between">
        <h1 className="font-medium text-sm">Culture</h1>
        <Button
          type="button"
          size="xs"
          variant="ghost"
          className="flex items-center gap-1"
        >
          <PlusIcon className="size-3.5" />
          Create Entry
        </Button>
      </div>
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                return (
                  <TableHead key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows?.length ? (
            table.getRowModel().rows.map((row) => (
              <TableRow
                key={row.id}
                data-state={row.getIsSelected() && "selected"}
              >
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={columns.length} className="h-24 text-center">
                No results.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}
